'use client';

import { useClientTranslation } from '@lib/i18n/client';
import { Icon } from '@tuic/web-compositions/core/Icon';
import { twJoin } from 'tailwind-merge';

import { ConnectionStatus } from '../lib/connection-status';

import type { IconName } from '@tuic/web-compositions/core/Icon';
import type { FC } from 'react';

type Props = {
  status: ConnectionStatus;
};

const iconFor = (status: ConnectionStatus): IconName =>
  status === ConnectionStatus.NoConnection ? 'no-wifi' : 'wifi';

/**
 * Small status badge rendered as the `tag` on the XiCafe internet card.
 *
 * Visually matches the GetSlash `ConnectionStatusSymbol` (Active = green dot,
 * Paused = red dot, Exhausted = no dot, Connected / Unknown = icon only), but
 * is implemented independently so the XiCafe module never imports from
 * `@modules/get-slash-internet-card`.
 */
export const XiCafeConnectionStatusSymbol: FC<Props> = ({ status }) => {
  const { t } = useClientTranslation();
  const label = renderLabel(t, status);

  return (
    <div
      className={twJoin(
        'text-marine-high-emphasis bg-white px-3 rounded-md',
        label ? 'py-1' : 'py-2',
      )}
    >
      <Icon name={iconFor(status)} size="md" className="block mx-auto" />
      {label && (
        <div className="flex items-center justify-center gap-1 font-semibold text-xs">
          {label}
        </div>
      )}
    </div>
  );
};

const renderLabel = (
  t: ReturnType<typeof useClientTranslation>['t'],
  status: ConnectionStatus,
): React.ReactNode => {
  if (status === ConnectionStatus.Active) {
    return (
      <>
        <div>{t('aktiv')}</div>
        <div className="size-2 bg-success-100 rounded-full" />
      </>
    );
  }

  if (status === ConnectionStatus.Paused) {
    return (
      <>
        <div>{t('pausiert')}</div>
        <div className="size-2 bg-error-100 rounded-full" />
      </>
    );
  }

  return null;
};
